import { useEffect } from 'react'
import { useMeState } from '../lib/me.js'
import { TRAINING_FILES_OFF_TH, TRAINING_FILES_OFF_DETAIL_TH } from '../lib/training.js'
import { useToast } from '../kit'
import BrandMark from './BrandMark.jsx'

// Training sandbox: no file reaches Wasabi (src/lib/training.js says why). Rather than a
// flag in each of the ~15 upload screens, ONE capture-phase listener refuses every file
// picker and every file drop before React sees it — so a screen added next month is covered
// without anyone remembering. A hidden <input type="file"> opened by a button's
// ref.click() still dispatches a click, and a cancelled click never opens the picker.
// The buttons stay visible on purpose: trainees learn where they are. The server refuses
// as well (functions/_middleware.js); this copy only explains it.
function TrainingFileGuard() {
  const toast = useToast()
  useEffect(() => {
    const refuse = (e) => {
      e.preventDefault()
      e.stopPropagation()
      toast.info(TRAINING_FILES_OFF_TH, { detail: TRAINING_FILES_OFF_DETAIL_TH })
    }
    const onClick = (e) => {
      if (e.target instanceof HTMLInputElement && e.target.type === 'file') refuse(e)
    }
    const onDrop = (e) => {
      if (Array.from(e.dataTransfer?.types || []).includes('Files')) refuse(e)
    }
    document.addEventListener('click', onClick, true)
    document.addEventListener('drop', onDrop, true)
    return () => {
      document.removeEventListener('click', onClick, true)
      document.removeEventListener('drop', onDrop, true)
    }
  }, [toast])
  return null
}

/**
 * App-wide auth gate. Nothing inside renders until the current user is
 * confirmed via /__app/me:
 *   - 'loading' → a minimal splash (never the app chrome)
 *   - 'error'   → render nothing; me.js has already called forceLogout(), so the
 *                 browser is on its way to /__app/logout → login. We never show a
 *                 page when the user (or their token) can't be retrieved.
 *   - 'ok'      → render the app.
 */
export default function AuthGate({ children }) {
  const { status, me } = useMeState()

  if (status === 'ok') {
    if (!me?.training) return children
    // Training sandbox: an amber frame round the whole window plus a label that
    // never goes away — two identical-looking CRM tabs is how a real patient gets
    // booked into the sandbox. Both are pointer-events:none overlays, so no page
    // layout moves.
    return (
      <>
        {children}
        <TrainingFileGuard />
        <div aria-hidden="true" className="pointer-events-none fixed inset-0 z-[9998] border-4 border-amber-400" />
        <div className="pointer-events-none fixed bottom-3 left-1/2 z-[9999] -translate-x-1/2 rounded-full bg-amber-400 px-4 py-1.5 text-[13px] font-semibold text-amber-950 shadow-lg">
          โหมดฝึกอบรม — ข้อมูลทั้งหมดเป็นข้อมูลสมมติ
        </div>
      </>
    )
  }

  if (status === 'loading') {
    return (
      <div className="h-full min-h-screen flex flex-col items-center justify-center gap-3 text-slate-400">
        <BrandMark size={40} className="animate-pulse" />
        <div className="text-[13px]">กำลังตรวจสอบสิทธิ์…</div>
      </div>
    )
  }

  // status === 'error' → forceLogout() is redirecting to login; show nothing.
  return null
}
